import {
  useCallback,
  useEffect,
  useLayoutEffect, useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useTopLayer } from '@/hooks/useTopLayer';
import { cn } from '@/lib/utils';

export type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'right-start' | 'left-start' | 'top' | 'bottom' | 'right';

type Anchor = RefObject<HTMLElement | null> | { x: number; y: number };

const GAP = 8;
const MARGIN = 8;

function computePosition(anchor: DOMRect, panel: { width: number; height: number }, placement: Placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = 0;
  let left = 0;
  switch (placement) {
    case 'bottom-start':
      top = anchor.bottom + GAP;
      left = anchor.left;
      if (top + panel.height > vh - MARGIN) top = anchor.top - GAP - panel.height;
      break;
    case 'bottom-end':
      top = anchor.bottom + GAP;
      left = anchor.right - panel.width;
      if (top + panel.height > vh - MARGIN) top = anchor.top - GAP - panel.height;
      break;
    case 'top-start':
      top = anchor.top - GAP - panel.height;
      left = anchor.left;
      if (top < MARGIN) top = anchor.bottom + GAP;
      break;
    case 'top-end':
      top = anchor.top - GAP - panel.height;
      left = anchor.right - panel.width;
      if (top < MARGIN) top = anchor.bottom + GAP;
      break;
    case 'top':
      top = anchor.top - GAP - panel.height;
      left = anchor.left + anchor.width / 2 - panel.width / 2;
      if (top < MARGIN) top = anchor.bottom + GAP;
      break;
    case 'bottom':
      top = anchor.bottom + GAP;
      left = anchor.left + anchor.width / 2 - panel.width / 2;
      if (top + panel.height > vh - MARGIN) top = anchor.top - GAP - panel.height;
      break;
    case 'right':
      top = anchor.top + anchor.height / 2 - panel.height / 2;
      left = anchor.right + GAP;
      break;
    case 'right-start':
      top = anchor.top;
      left = anchor.right + GAP;
      if (left + panel.width > vw - MARGIN) left = anchor.left - GAP - panel.width;
      break;
    case 'left-start':
      top = anchor.top;
      left = anchor.left - GAP - panel.width;
      if (left < MARGIN) left = anchor.right + GAP;
      break;
  }
  left = Math.min(Math.max(MARGIN, left), vw - panel.width - MARGIN);
  top = Math.min(Math.max(MARGIN, top), vh - panel.height - MARGIN);
  return { top, left };
}

function anchorRect(anchor: Anchor): DOMRect | null {
  if ('x' in anchor) return new DOMRect(anchor.x, anchor.y, 0, 0);
  return anchor.current?.getBoundingClientRect() ?? null;
}

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchor: Anchor;
  placement?: Placement;
  children: ReactNode;
  className?: string;
  role?: string;
  label?: string;
  /** Focus the first focusable element when opened (default true). */
  autoFocus?: boolean;
}

/**
 * Floating panel rendered in a portal and positioned against an anchor element
 * or point. Closes on outside click, Escape, and window resize.
 */
/**
 * Where floating layers mount. While a modal <dialog> is open everything
 * outside it is inert, so layers opened from inside one must live inside it
 * (the top layer still positions them against the viewport).
 */
function layerRoot(): HTMLElement {
  const modals = document.querySelectorAll<HTMLDialogElement>('dialog[open]');
  return modals[modals.length - 1] ?? document.body;
}

export function Popover({ open, onClose, anchor, placement = 'bottom-start', children, className, role = 'dialog', label, autoFocus = true }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const restoreFocus = useRef<Element | null>(null);
  // Refs keep the effects below from re-running (and stealing focus) when the
  // parent re-renders with a new callback or anchor object.
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Must run before positioning: a closed [popover] is display:none and can't be measured.
  useTopLayer(panelRef, open);

  const reposition = useCallback(() => {
    const rect = anchorRect(anchorRef.current);
    const panel = panelRef.current;
    if (!rect || !panel) return;
    const next = computePosition(rect, { width: panel.offsetWidth, height: panel.offsetHeight }, placement);
    setPos((prev) => (prev && prev.top === next.top && prev.left === next.left ? prev : next));
  }, [placement]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    reposition();
    const observer = new ResizeObserver(reposition);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    restoreFocus.current = document.activeElement;
    // Wait a frame: the panel stays `visibility: hidden` until it has been positioned,
    // and hidden elements can't receive focus.
    const focusFrame = autoFocus
      ? requestAnimationFrame(() => {
          const focusable = panelRef.current?.querySelector<HTMLElement>('input, textarea, [role="menuitem"], button, [tabindex="0"]');
          focusable?.focus({ preventScroll: true });
        })
      : 0;
    const close = () => onCloseRef.current();
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      const a = anchorRef.current;
      if (!('x' in a) && a.current?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Also stops an enclosing <dialog> from treating it as its own close request.
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    };
    const onResize = () => close();
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      const el = restoreFocus.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus({ preventScroll: true });
    };
  }, [open, autoFocus]);

  // Resolved once per opening so the layer doesn't hop between containers mid-life.
  const root = useMemo(() => (open ? layerRoot() : null), [open]);
  if (!open || !root) return null;
  return createPortal(
    <div
      ref={panelRef}
      role={role}
      aria-label={label}
      popover="manual"
      style={{ position: 'fixed', inset: 'auto', top: pos?.top ?? -9999, left: pos?.left ?? -9999, zIndex: 60, margin: 0 }}
      className={cn('animate-pop-in overflow-visible rounded-2xl border border-line bg-elevated p-0 text-fg shadow-pop', !pos && 'invisible', className)}
    >
      {children}
    </div>,
    root,
  );
}

/* ------------------------------------------------------------------ */
/* Menus                                                               */
/* ------------------------------------------------------------------ */

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  hint?: string;
}

export type MenuEntry = MenuItem | 'separator' | null | false;

export function Menu({
  open,
  onClose,
  anchor,
  placement,
  items,
  label = 'Menu',
}: {
  open: boolean;
  onClose: () => void;
  anchor: Anchor;
  placement?: Placement;
  items: MenuEntry[];
  label?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      buttons[(index + 1) % buttons.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
    } else if (e.key === 'Home') buttons[0]?.focus();
    else if (e.key === 'End') buttons.at(-1)?.focus();
    else if (e.key === 'Tab') onClose();
  };

  const visible = items.filter(Boolean) as Array<MenuItem | 'separator'>;
  return (
    <Popover open={open} onClose={onClose} anchor={anchor} placement={placement} role="menu" label={label} className="min-w-[200px] p-1.5">
      <div ref={listRef} onKeyDown={onKeyDown}>
        {visible.map((item, i) =>
          item === 'separator' ? (
            <div key={`sep-${i}`} role="separator" className="mx-2 my-1 h-px bg-line" />
          ) : (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                onClose();
                item.onSelect();
              }}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-full px-3 py-2 text-left text-sm font-medium transition-colors outline-none disabled:opacity-40',
                item.danger ? 'text-danger hover:bg-danger hover:text-white focus:bg-danger focus:text-white' : 'text-fg-soft hover:bg-accent hover:text-accent-fg focus:bg-accent focus:text-accent-fg',
              )}
            >
              {item.icon && <span className="shrink-0 opacity-80">{item.icon}</span>}
              <span className="flex-1 truncate">{item.label}</span>
              {item.hint && <span className="text-xs opacity-60">{item.hint}</span>}
            </button>
          ),
        )}
      </div>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/* Tooltip                                                             */
/* ------------------------------------------------------------------ */

export function Tooltip({ label, children, placement = 'top', className }: { label: ReactNode; children: ReactNode; placement?: Placement; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const show = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), 250);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <span ref={ref} className={cn('inline-flex', className)} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} onMouseDown={hide}>
      {children}
      {open &&
        createPortal(<TooltipBubble anchor={ref} placement={placement} label={label} />, layerRoot())}
    </span>
  );
}

function TooltipBubble({ anchor, placement, label }: { anchor: RefObject<HTMLElement | null>; placement: Placement; label: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useTopLayer(ref, true);
  useLayoutEffect(() => {
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect || !ref.current) return;
    setPos(computePosition(rect, { width: ref.current.offsetWidth, height: ref.current.offsetHeight }, placement));
  }, [anchor, placement]);
  return (
    <div
      ref={ref}
      role="tooltip"
      popover="manual"
      style={{ position: 'fixed', inset: 'auto', top: pos?.top ?? -9999, left: pos?.left ?? -9999, zIndex: 70, margin: 0 }}
      className="pointer-events-none max-w-[260px] animate-fade-in overflow-visible rounded-full border-0 bg-[#2e2b25] px-3 py-1.5 text-[13px] font-semibold text-[#f5ead8] shadow-pop"
    >
      {label}
    </div>
  );
}
